// /quest/$questId — interactive narrative fiction surface.
//
// Reads a Quest from the static `data/quests` registry and walks the
// user through it beat-by-beat. The user chooses one option per beat;
// each choice advances to a named beat (with branches converging at
// pacing points so the writing remains human-curatable). Some beats
// award a Memory Shard fragment, surfaced as an inline aside.
//
// Phase-1 storage: state lives in localStorage under
// `athrelaris.quest.<questId>` — no server roundtrip per choice. This
// keeps the prototype shippable without a `quest_runs` table; once
// the format is validated we'll migrate state server-side.

import type { BondDto, EmberTone, Quest, QuestBeat, QuestState } from '@athrelaris/shared';
import { Link, createFileRoute, redirect } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getQuest } from '../data/quests/index.js';
import { switchLocaleToBond } from '../i18n/index.js';
import {
  getMyBond,
  getQuestShardsUnlocked,
  persistQuestShard,
  recordQuestRelationshipNudge,
  unlockQuestEchoform,
} from '../lib/api';
import { getSessionSafe } from '../lib/auth-client';
import { redirectIfLandingOnly } from '../lib/landing-mode';
import { useEmberTheme } from '../lib/use-ember-theme';

export const Route = createFileRoute('/quest/$questId')({
  beforeLoad: async () => {
    redirectIfLandingOnly();
    const data = await getSessionSafe();
    if (!data?.session) throw redirect({ to: '/login' });
  },
  loader: async ({ params }) => {
    const bond = await getMyBond();
    if (!bond) throw redirect({ to: '/awakening' });
    const quest = getQuest(params.questId);
    if (!quest) throw redirect({ to: '/inbox' });
    // Pre-fetch shards already recovered for this quest so the UI can
    // suppress the celebration card on replays. Soft-fail to empty
    // list — the worst case is the card shows once on a known shard.
    const previouslyUnlockedShardIds = await getQuestShardsUnlocked(params.questId).catch(
      () => [] as string[],
    );
    return { bond, quest, previouslyUnlockedShardIds };
  },
  component: QuestPage,
});

function storageKey(questId: string): string {
  return `athrelaris.quest.${questId}`;
}

function loadState(questId: string): QuestState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(storageKey(questId));
    if (!raw) return null;
    return JSON.parse(raw) as QuestState;
  } catch {
    return null;
  }
}

function saveState(state: QuestState): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey(state.questId), JSON.stringify(state));
  } catch {
    // localStorage may be full or disabled — soft-fail. The session
    // will keep its in-memory state; reloading the tab loses progress.
  }
}

// Replace every occurrence of the literal `{emberName}` placeholder
// with the bound player's Ember name. Writer agents emit this token
// wherever they would otherwise write a third-person reference to the
// Ember (e.g., "Your Ember crosses..." → "{emberName} crosses..."), so
// every bond reads narration with their own Ember named.
function interpolateEmberName(text: string, emberName: string): string {
  return text.replaceAll('{emberName}', emberName);
}

function newState(questId: string, startBeatId: string): QuestState {
  const now = new Date().toISOString();
  return {
    questId,
    currentBeatId: startBeatId,
    choices: [],
    shardsUnlocked: [],
    startedAt: now,
    completedAt: null,
  };
}

// Build the body of the memory_shard letter for a given shard reward
// in the user's locale, then fire-and-forget the persist call. The
// server is idempotent on (questId, shardId), so re-emitting on a
// reset run won't duplicate the shard in /memories.
function persistShard(quest: Quest, beatId: string, locale: 'en' | 'es'): void {
  const shard = quest.beats[beatId]?.memoryShard;
  if (!shard) return;
  const name = shard.name[locale];
  const fragment = shard.fragment[locale];
  const body = `${name}.\n\n${fragment}`;
  persistQuestShard({
    questId: quest.id,
    shardId: shard.id,
    body,
  }).catch((err) => {
    // Soft-fail — the shard is still visible in-quest. A reload from
    // /memories may not show it until the next successful save, but
    // the player's experience inside the quest is unaffected.
    // eslint-disable-next-line no-console
    console.warn('persistQuestShard failed', err);
  });
}

function QuestPage() {
  const { bond, quest, previouslyUnlockedShardIds } = Route.useLoaderData();
  const previouslyUnlockedSet = useMemo(
    () => new Set(previouslyUnlockedShardIds),
    [previouslyUnlockedShardIds],
  );
  const locale = bond.locale === 'es' ? 'es' : 'en';

  useEmberTheme(bond.emberColorPalette);
  useEffect(() => {
    switchLocaleToBond(bond.locale);
  }, [bond.locale]);

  const [state, setState] = useState<QuestState | null>(() => loadState(quest.id));
  const [stage, setStage] = useState<'intro' | 'beat' | 'complete'>(() => {
    const s = loadState(quest.id);
    if (!s) return 'intro';
    if (s.completedAt) return 'complete';
    return 'beat';
  });

  const begin = useCallback(() => {
    const fresh = newState(quest.id, quest.startBeatId);
    const beat = quest.beats[quest.startBeatId];
    if (beat?.memoryShard) {
      fresh.shardsUnlocked = [...fresh.shardsUnlocked, fresh.currentBeatId];
      persistShard(quest, fresh.currentBeatId, locale);
    }
    saveState(fresh);
    setState(fresh);
    setStage('beat');
  }, [quest, locale]);

  const resetRun = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(storageKey(quest.id));
    }
    setState(null);
    setStage('intro');
  }, [quest.id]);

  const choose = useCallback(
    (beatId: string, choiceId: string, nextBeatId: string) => {
      if (!state) return;
      const now = new Date().toISOString();
      const nextBeat = quest.beats[nextBeatId];
      if (!nextBeat) return;
      const earnedShard = Boolean(
        nextBeat.memoryShard && !state.shardsUnlocked.includes(nextBeatId),
      );
      const next: QuestState = {
        ...state,
        currentBeatId: nextBeatId,
        choices: [...state.choices, { beatId, choiceId, at: now }],
        shardsUnlocked: earnedShard ? [...state.shardsUnlocked, nextBeatId] : state.shardsUnlocked,
        completedAt: nextBeat.choices.length === 0 ? now : null,
      };
      saveState(next);
      setState(next);
      if (earnedShard) {
        // Persist the shard to the bond's letter feed (memory_shard
        // letter) so it surfaces under /memories alongside the in-
        // world recoveries from the letter pipeline.
        persistShard(quest, nextBeatId, locale);
      }
      // If the chosen choice carries a relationship nudge, fire-and-
      // forget the audit log. Server only logs in Phase 1; the
      // player's felt change comes from the choice's emberFollowup
      // rendered on the next beat.
      const prevBeat = quest.beats[beatId];
      const chosen = prevBeat?.choices.find((c) => c.id === choiceId);
      if (chosen?.relationshipNudge) {
        recordQuestRelationshipNudge({
          questId: quest.id,
          beatId,
          choiceId,
          direction: chosen.relationshipNudge.direction,
          magnitude: chosen.relationshipNudge.magnitude,
        }).catch((err) => {
          // eslint-disable-next-line no-console
          console.warn('recordQuestRelationshipNudge failed', err);
        });
      }
    },
    [state, quest, locale],
  );

  if (stage === 'intro' || !state) {
    return <QuestIntro quest={quest} locale={locale} onBegin={begin} bond={bond} />;
  }

  const beat = quest.beats[state.currentBeatId];
  if (!beat) {
    // Defensive: corrupted state pointing at a beat that no longer
    // exists. Reset rather than crash; the prototype is forgiving.
    return <QuestCorrupted onReset={resetRun} />;
  }

  return (
    <QuestBeatView
      quest={quest}
      bond={bond}
      beat={beat}
      state={state}
      locale={locale}
      previouslyUnlockedShardIds={previouslyUnlockedSet}
      onChoose={choose}
      onReset={resetRun}
    />
  );
}

function QuestIntro({
  quest,
  locale,
  bond,
  onBegin,
}: {
  quest: Quest;
  locale: 'en' | 'es';
  bond: BondDto;
  onBegin: () => void;
}) {
  const { t } = useTranslation();
  return (
    <main className="mx-auto max-w-2xl">
      <nav className="mt-12 mb-10 font-display text-[0.7rem] text-ash-500 uppercase tracking-[0.34em] sm:mt-8 sm:text-xs lg:mt-4">
        <Link to="/echoes" className="transition hover:text-ash-200">
          {locale === 'es' ? '← Ecos' : '← Echoes'}
        </Link>
      </nav>

      <header className="mb-12 text-center animate-fadeUp lg:mb-16">
        <p className="font-display text-[0.65rem] text-accent-strong uppercase tracking-[0.4em] sm:text-xs lg:text-[0.78rem]">
          {locale === 'es' ? 'Una salida' : 'A walking'}
        </p>
        <h1
          className="mt-4 font-display text-3xl text-ash-50 tracking-wide sm:text-4xl lg:text-5xl"
          style={{ fontWeight: 500 }}
        >
          {quest.title[locale]}
        </h1>
        <p className="mx-auto mt-6 max-w-xl font-body text-ash-300 italic leading-relaxed sm:text-lg">
          {quest.subtitle[locale]}
        </p>
        <div className="mx-auto mt-10 flex max-w-xs items-center gap-3">
          <span className="hairline h-px flex-1" />
          <span aria-hidden="true" className="block h-1 w-1 rounded-full bg-accent" />
          <span className="hairline h-px flex-1" />
        </div>
      </header>

      <section
        className="rounded-sm border border-ash-700/40 bg-paper-900/30 p-6 text-center animate-fadeUp lg:p-8"
        style={{ animationDelay: '0.2s' }}
      >
        <p className="font-body text-ash-300 italic leading-relaxed">
          {locale === 'es'
            ? `${bond.emberName} te espera al borde del claro. Llevará el rato que lleve.`
            : `${bond.emberName} is waiting at the edge of the clearing. It will take what time it takes.`}
        </p>
        <button
          type="button"
          onClick={onBegin}
          className="mt-7 inline-block rounded-sm border border-accent/60 bg-paper-900/60 px-6 py-3 font-display text-[0.7rem] text-accent-strong uppercase tracking-[0.4em] transition hover:border-accent hover:bg-paper-800/60 hover:text-ash-100"
        >
          {locale === 'es' ? 'Cruzar el umbral' : 'Step into the clearing'}
        </button>
      </section>
    </main>
  );
}

function QuestBeatView({
  quest,
  bond,
  beat,
  state,
  locale,
  previouslyUnlockedShardIds,
  onChoose,
  onReset,
}: {
  quest: Quest;
  bond: BondDto;
  beat: QuestBeat;
  state: QuestState;
  locale: 'en' | 'es';
  previouslyUnlockedShardIds: ReadonlySet<string>;
  onChoose: (beatId: string, choiceId: string, nextBeatId: string) => void;
  onReset: () => void;
}) {
  const { t } = useTranslation();
  const paragraphs = beat.narration[locale];
  const isTerminal = beat.choices.length === 0;
  const shardCount = state.shardsUnlocked.length;

  // The follow-up reaction the Ember has *after* the player's last
  // choice — pulled from the choice the player just selected, not from
  // the beat itself. Rendered above the new scene so it acknowledges
  // the decision before the player walks into what follows.
  const emberFollowup = useMemo(() => {
    const last = state.choices[state.choices.length - 1];
    if (!last) return null;
    const prevBeat = quest.beats[last.beatId];
    const choice = prevBeat?.choices.find((c) => c.id === last.choiceId);
    return choice?.emberFollowup ?? null;
  }, [state.choices, quest.beats]);

  return (
    <main className="mx-auto max-w-2xl">
      <nav className="mt-12 mb-8 flex items-center justify-between font-display text-[0.7rem] text-ash-500 uppercase tracking-[0.34em] sm:mt-8 sm:text-xs lg:mt-4">
        <Link to="/echoes" className="transition hover:text-ash-200">
          {locale === 'es' ? '← Ecos' : '← Echoes'}
        </Link>
        <span className="text-ash-600">
          {locale === 'es'
            ? `${shardCount} memorias`
            : `${shardCount} memor${shardCount === 1 ? 'y' : 'ies'}`}
        </span>
      </nav>

      {emberFollowup && (
        <EmberAside
          ember={emberFollowup}
          emberName={bond.emberName}
          locale={locale}
          variant="followup"
        />
      )}

      <ScenePanel key={beat.scene} quest={quest} scene={beat.scene} />

      <article className="letter mt-10 animate-fadeUp text-[1.06rem] text-ash-100 sm:text-[1.12rem] lg:text-[1.18rem]">
        {paragraphs.map((p) => {
          const text = interpolateEmberName(p, bond.emberName);
          return <p key={`${beat.id}-${p.slice(0, 48)}`}>{text}</p>;
        })}
      </article>

      {beat.ember && <EmberAside ember={beat.ember} emberName={bond.emberName} locale={locale} />}

      {beat.memoryShard && !previouslyUnlockedShardIds.has(beat.memoryShard.id) && (
        <MemoryShardCard shard={beat.memoryShard} emberName={bond.emberName} locale={locale} />
      )}

      {beat.echoformUnlock && (
        <EchoformUnlockCard questId={quest.id} echoform={beat.echoformUnlock} locale={locale} />
      )}

      {beat.choices.length > 0 ? (
        <ChoiceList
          beatId={beat.id}
          choices={beat.choices}
          emberName={bond.emberName}
          locale={locale}
          onChoose={onChoose}
        />
      ) : (
        <TerminalFooter onReset={onReset} locale={locale} />
      )}

      {!isTerminal && (
        <footer className="mt-14 border-paper-800/50 border-t pt-6 text-center font-body text-ash-600 text-xs italic">
          <button type="button" onClick={onReset} className="transition hover:text-ash-400">
            {locale === 'es' ? 'Volver al principio del claro' : 'Walk back to the edge'}
          </button>
        </footer>
      )}
    </main>
  );
}

function ScenePanel({ quest, scene }: { quest: Quest; scene: string }) {
  const [imageOk, setImageOk] = useState(true);
  const src = `/quests/${quest.id}/scenes/${scene}.png`;

  if (!scene) return null;
  if (!imageOk) return <SceneAtmosphericPlaceholder scene={scene} />;

  return (
    <figure className="overflow-hidden rounded-sm border border-ash-700/40">
      <img
        src={src}
        alt=""
        className="block aspect-video w-full select-none object-cover"
        draggable={false}
        onError={() => setImageOk(false)}
      />
    </figure>
  );
}

// Hash the scene slug into a deterministic atmospheric gradient so the
// prototype renders something evocative even before generated images
// exist. The label is shown faintly to make it obvious which scene
// would normally fill the panel.
function SceneAtmosphericPlaceholder({ scene }: { scene: string }) {
  const hue = useMemo(() => {
    let h = 0;
    for (let i = 0; i < scene.length; i += 1) {
      h = (h * 31 + scene.charCodeAt(i)) % 360;
    }
    return h;
  }, [scene]);

  return (
    <figure
      className="relative flex aspect-video w-full items-end overflow-hidden rounded-sm border border-ash-700/40"
      style={{
        background: `linear-gradient(140deg, hsl(${hue}, 18%, 14%) 0%, hsl(${(hue + 30) % 360}, 22%, 22%) 60%, hsl(${(hue + 60) % 360}, 14%, 10%) 100%)`,
      }}
    >
      <figcaption className="m-4 font-display text-[0.55rem] text-ash-500/70 uppercase tracking-[0.34em]">
        {scene}
      </figcaption>
    </figure>
  );
}

function EmberAside({
  ember,
  emberName,
  locale,
  variant = 'inline',
}: {
  ember: NonNullable<QuestBeat['ember']>;
  emberName: string;
  locale: 'en' | 'es';
  variant?: 'inline' | 'followup';
}) {
  // The "followup" variant sits above the next scene as the Ember's
  // immediate reaction to the player's last decision. Same styling
  // language, slightly different spacing + a subtle eyebrow that names
  // it as a response rather than ambient presence.
  const isFollowup = variant === 'followup';
  return (
    <aside
      className={`rounded-sm border-l-2 bg-paper-900/40 px-5 py-4 animate-fadeUp lg:px-6 lg:py-5 ${
        isFollowup ? 'mb-8 lg:mb-10' : 'mt-8 lg:mt-10'
      } ${toneBorder(ember.tone)}`}
      style={{ animationDelay: isFollowup ? '0.05s' : '0.2s' }}
    >
      <p
        className={`font-display text-[0.6rem] uppercase tracking-[0.34em] ${toneLabel(ember.tone)}`}
      >
        {isFollowup
          ? `${emberName} · ${locale === 'es' ? 'tras tu elección' : 'after your choice'}`
          : emberName}
      </p>
      <p className="mt-2 font-body text-ash-200 italic leading-relaxed sm:text-[0.98rem]">
        {interpolateEmberName(ember.text[locale], emberName)}
      </p>
    </aside>
  );
}

// Names per echoform, locale-aware. Kept inline (rather than i18n
// keys) because the quest surface uses literal Athrelaris lore copy
// rather than the generic letter UI strings, and adding three tiny
// dictionary entries to a global locale file would obscure that.
const ECHOFORM_DISPLAY: Record<
  NonNullable<QuestBeat['echoformUnlock']>,
  { en: { label: string; tagline: string }; es: { label: string; tagline: string } }
> = {
  codex: {
    en: {
      label: 'Codex Echoform',
      tagline: 'A form that remembers being named.',
    },
    es: {
      label: 'Codex Echoform',
      tagline: 'Una forma que recuerda haber sido nombrada.',
    },
  },
  dream: {
    en: {
      label: 'Dream Echoform',
      tagline: 'A form that travels in sleep.',
    },
    es: {
      label: 'Dream Echoform',
      tagline: 'Una forma que viaja en el sueño.',
    },
  },
  deep: {
    en: {
      label: 'Deep Echoform',
      tagline: 'A form that holds what cannot be carried alone.',
    },
    es: {
      label: 'Deep Echoform',
      tagline: 'Una forma que sostiene lo que no se puede cargar a solas.',
    },
  },
};

function EchoformUnlockCard({
  questId,
  echoform,
  locale,
}: {
  questId: string;
  echoform: NonNullable<QuestBeat['echoformUnlock']>;
  locale: 'en' | 'es';
}) {
  // Fire the unlock once when this card mounts. The server is
  // idempotent on (bondId, echoform) so the second invocation when
  // the user replays the quest returns `alreadyUnlocked: true` and
  // does not double-enqueue the image job.
  useEffect(() => {
    unlockQuestEchoform({ echoform, questId }).catch((err) => {
      // eslint-disable-next-line no-console
      console.warn('unlockQuestEchoform failed', err);
    });
  }, [echoform, questId]);

  const display = ECHOFORM_DISPLAY[echoform][locale];
  return (
    <section
      className="mt-10 rounded-sm border border-accent-strong/60 bg-paper-900/40 p-6 animate-fadeUp lg:mt-12 lg:p-7"
      style={{ animationDelay: '0.5s' }}
    >
      <p className="font-display text-[0.62rem] text-accent-strong uppercase tracking-[0.4em]">
        {locale === 'es' ? 'Un Echoform despertó' : 'An Echoform stirred'}
      </p>
      <h3 className="mt-2 font-display text-ash-50 text-xl tracking-wide">{display.label}</h3>
      <p className="mt-3 font-body text-ash-200 italic leading-relaxed">{display.tagline}</p>
      <p className="mt-4 font-display text-[0.58rem] text-ash-500 uppercase tracking-[0.34em]">
        {locale === 'es'
          ? 'Aparecerá en tu cuenta en unos instantes.'
          : 'It will appear in your account in a few moments.'}
      </p>
    </section>
  );
}

function MemoryShardCard({
  shard,
  emberName,
  locale,
}: {
  shard: NonNullable<QuestBeat['memoryShard']>;
  emberName: string;
  locale: 'en' | 'es';
}) {
  return (
    <section
      className="mt-10 rounded-sm border border-accent/40 bg-paper-900/30 p-6 animate-fadeUp lg:mt-12 lg:p-7"
      style={{ animationDelay: '0.4s' }}
    >
      <p className="font-display text-[0.62rem] text-accent-strong uppercase tracking-[0.4em]">
        {locale === 'es' ? 'Memoria preservada' : 'A memory preserved'}
      </p>
      <h3 className="mt-2 font-display text-ash-50 text-xl tracking-wide">{interpolateEmberName(shard.name[locale], emberName)}</h3>
      <p className="mt-3 font-body text-ash-200 italic leading-relaxed">{interpolateEmberName(shard.fragment[locale], emberName)}</p>
    </section>
  );
}

function ChoiceList({
  beatId,
  choices,
  emberName,
  locale,
  onChoose,
}: {
  beatId: string;
  choices: QuestBeat['choices'];
  emberName: string;
  locale: 'en' | 'es';
  onChoose: (beatId: string, choiceId: string, nextBeatId: string) => void;
}) {
  return (
    <section
      className="mt-12 rounded-sm border border-accent/40 bg-paper-900/40 p-6 animate-fadeUp lg:mt-16 lg:p-8"
      style={{ animationDelay: '0.6s' }}
    >
      <p className="font-display text-[0.65rem] text-accent-strong uppercase tracking-[0.34em]">
        {locale === 'es' ? 'Qué hacéis' : 'What you do'}
      </p>
      <ul className="mt-4 space-y-3">
        {choices.map((c) => {
          const proposed = c.emberProposed === true;
          return (
            <li key={c.id}>
              {proposed && (
                <p className="mb-1.5 ml-1 font-display text-[0.56rem] text-accent-strong/85 uppercase tracking-[0.34em]">
                  {locale === 'es'
                    ? `${emberName} se inclina por esto`
                    : `${emberName} leans toward this`}
                </p>
              )}
              <button
                type="button"
                onClick={() => onChoose(beatId, c.id, c.nextBeatId)}
                className={`group block w-full rounded-sm border bg-paper-900/30 px-4 py-3 text-left font-body italic transition hover:bg-paper-800/40 ${
                  proposed
                    ? 'border-accent-strong/60 text-ash-100 hover:border-accent-strong/80'
                    : 'border-ash-700/40 text-ash-200 hover:border-accent/60 hover:text-ash-100'
                }`}
              >
                <span
                  className={`mr-3 ${
                    proposed
                      ? 'text-accent-strong group-hover:text-accent-strong'
                      : 'text-accent group-hover:text-accent-strong'
                  }`}
                >
                  ◇
                </span>
                {interpolateEmberName(c.label[locale], emberName)}
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function TerminalFooter({
  onReset,
  locale,
}: {
  onReset: () => void;
  locale: 'en' | 'es';
}) {
  return (
    <footer className="mt-14 border-paper-800/50 border-t pt-10 text-center font-body text-ash-400 italic lg:mt-20">
      <p className="text-ash-300">
        {locale === 'es'
          ? 'Habéis cruzado el claro. La piedra queda detrás.'
          : 'You have walked the clearing. The stone stays behind.'}
      </p>
      <div className="mt-6 flex items-center justify-center gap-6 text-ash-500 text-xs">
        <Link to="/echoes" className="transition hover:text-ash-300">
          {locale === 'es' ? 'Volver a los ecos' : 'Return to echoes'}
        </Link>
        <span aria-hidden="true" className="text-ash-700">
          ·
        </span>
        <button type="button" onClick={onReset} className="transition hover:text-ash-300">
          {locale === 'es' ? 'Caminar el claro otra vez' : 'Walk the clearing again'}
        </button>
      </div>
    </footer>
  );
}

function QuestCorrupted({ onReset }: { onReset: () => void }) {
  return (
    <main className="mx-auto max-w-2xl">
      <div className="mt-20 rounded-sm border border-ash-700/40 bg-paper-900/30 p-8 text-center">
        <p className="font-body text-ash-300 italic">The path forgot itself. Begin again.</p>
        <button
          type="button"
          onClick={onReset}
          className="mt-6 inline-block rounded-sm border border-accent/60 bg-paper-900/60 px-5 py-2 font-display text-[0.7rem] text-accent-strong uppercase tracking-[0.34em] transition hover:border-accent hover:text-ash-100"
        >
          Begin again
        </button>
      </div>
    </main>
  );
}

function toneBorder(tone: EmberTone): string {
  switch (tone) {
    case 'wary':
      return 'border-amber-600/40';
    case 'tender':
      return 'border-ash-300/40';
    case 'grieved':
      return 'border-paper-500/50';
    case 'alight':
      return 'border-accent-strong/60';
    case 'silent':
      return 'border-ash-700/40';
    default:
      return 'border-accent/40';
  }
}

function toneLabel(tone: EmberTone): string {
  switch (tone) {
    case 'wary':
      return 'text-amber-400/80';
    case 'tender':
      return 'text-ash-400';
    case 'grieved':
      return 'text-paper-300';
    case 'alight':
      return 'text-accent-strong';
    case 'silent':
      return 'text-ash-600';
    default:
      return 'text-accent';
  }
}
